import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { Radio, Plus, Trash2, Copy, WifiOff, Eye, Activity, Share2, ExternalLink, AlertCircle, Clock, Wifi, TrendingUp, ChevronDown, MonitorPlay, X, CircleDot, Code2, Sliders, Monitor, RefreshCw, MessageSquare, Volume2, VolumeX, Play, Sun, Moon, Maximize2, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import CustomVideoPlayer from './CustomVideoPlayer';

function Channels({ user, onLogout }) {
  const { t } = useLanguage();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [liveStatus, setLiveStatus] = useState({});
  const [expandedChannels, setExpandedChannels] = useState({});
  const [selectedProtocol, setSelectedProtocol] = useState({});
  const [playlists, setPlaylists] = useState([]);
  const [hlsStatus, setHlsStatus] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'live',
    playlist_id: ''
  });
  const [showEmbedModal, setShowEmbedModal] = useState(false);
  const [selectedChannelForEmbed, setSelectedChannelForEmbed] = useState(null);
  const [previewChannels, setPreviewChannels] = useState({});
  const [recordingStatus, setRecordingStatus] = useState({}); // { [channelId]: boolean }
  const [recordingStartTimes, setRecordingStartTimes] = useState({}); // { [channelId]: ISO string }
  const [recordingElapsed, setRecordingElapsed] = useState({}); // { [channelId]: "HH:MM:SS" }
  const [embedOptions, setEmbedOptions] = useState({
    autoplay: true,
    muted: true,
    chat: false,
    theme: 'dark',
    widthUnit: '%',
    widthValue: '100',
    height: '450',
    responsive: true,
    previewKey: 0  // increment to force iframe reload
  });
  const [copiedField, setCopiedField] = useState(null); // 'url' | 'iframe'
  const [chatModalChannel, setChatModalChannel] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    fetchChannels();
    fetchPlaylists();
    fetchLiveStatus();
    const liveInterval = setInterval(() => {
      fetchLiveStatus();
    }, 5000);
    const hlsInterval = setInterval(() => {
      if (channels.length > 0) {
        checkAllHlsStatus();
      }
    }, 10000); // Check HLS status every 10 seconds
    return () => {
      clearInterval(liveInterval);
      clearInterval(hlsInterval);
    };
  }, []);

  useEffect(() => {
    if (channels.length > 0) {
      checkAllHlsStatus();
    }
  }, [channels]);

  // Auto-close preview when channel goes offline
  useEffect(() => {
    setPreviewChannels(prev => {
      const next = { ...prev };
      let changed = false;
      for (const channel of channels) {
        const isLive = liveStatus[channel.id]?.isLive || hlsStatus[channel.id] || false;
        if (next[channel.id] && !isLive) {
          next[channel.id] = false;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [liveStatus, hlsStatus, channels]);

  // Format elapsed seconds → "HH:MM:SS"
  const formatElapsed = (startedAt) => {
    if (!startedAt) return '00:00:00';
    const sec = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return [h, m, s].map(n => String(n).padStart(2, '0')).join(':');
  };

  // Sync recording status from backend on mount and whenever channels load
  useEffect(() => {
    if (channels.length === 0) return;
    const token = localStorage.getItem('token');
    channels.forEach(async (ch) => {
      try {
        const res = await axios.get(`/api/channels/${ch.id}/recording/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.data.recording && res.data.info?.startedAt) {
          setRecordingStatus(prev => ({ ...prev, [ch.id]: true }));
          setRecordingStartTimes(prev => ({ ...prev, [ch.id]: res.data.info.startedAt }));
        } else {
          setRecordingStatus(prev => ({ ...prev, [ch.id]: false }));
          setRecordingStartTimes(prev => { const n = { ...prev }; delete n[ch.id]; return n; });
        }
      } catch { /* ignore */ }
    });
  }, [channels]);

  // Update elapsed display every second for active recordings
  useEffect(() => {
    const timer = setInterval(() => {
      setRecordingElapsed(prev => {
        const next = { ...prev };
        Object.entries(recordingStartTimes).forEach(([id, startedAt]) => {
          next[id] = formatElapsed(startedAt);
        });
        return next;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [recordingStartTimes]);

  const fetchPlaylists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/playlists', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaylists(response.data.playlists || []);
    } catch (error) {
      console.error('Error al cargar playlists:', error);
    }
  };

  const fetchChannels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/channels?limit=100', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const channelsData = response.data.channels || [];
      setChannels(channelsData);
      
      const defaultProtocols = {};
      channelsData.forEach(ch => {
        defaultProtocols[ch.id] = 'rtmp';
      });
      setSelectedProtocol(defaultProtocols);
      
      setLoading(false);
    } catch (error) {
      toast.error('Error al cargar canales');
      setLoading(false);
    }
  };

  const fetchLiveStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/channels/live-status/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const statusMap = {};
      response.data.liveChannels.forEach(ch => {
        statusMap[ch.channelId] = {
          isLive: ch.isLive,
          viewers: ch.viewers,
          state: ch.state
        };
      });
      setLiveStatus(statusMap);
    } catch (error) {
      console.error('Error fetching live status:', error);
    }
  };

  const checkAllHlsStatus = async () => {
    const statusPromises = channels
      .filter(channel => channel.stream_key)
      .map(async (channel) => {
        const isReady = await checkHlsStatus(channel);
        return { channelId: channel.id, isReady };
      });

    const results = await Promise.all(statusPromises);
    const newStatus = {};
    results.forEach(result => {
      newStatus[result.channelId] = result.isReady;
    });
    setHlsStatus(newStatus);
  };

  const checkHlsStatus = async (channel) => {
    if (!channel.stream_key) return false;
    try {
      const response = await fetch(`/api/stream/${channel.stream_key}/status`, { cache: 'no-store' });
      if (!response.ok) return false;
      const data = await response.json();
      return Boolean(data.online);
    } catch {
      return false;
    }
  };

  const handleCreateChannel = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/channels', formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Canal creado exitosamente');
      setShowModal(false);
      setFormData({ name: '', description: '', type: 'live', playlist_id: '' });
      fetchChannels();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear canal');
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este canal?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/channels/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Canal eliminado');
      fetchChannels();
    } catch (error) {
      toast.error('Error al eliminar canal');
    }
  };

  const togglePreview = (channelId) => {
    setPreviewChannels(prev => ({ ...prev, [channelId]: !prev[channelId] }));
  };

  const toggleRecording = async (channel) => {
    const token = localStorage.getItem('token');
    const isRec = recordingStatus[channel.id];
    try {
      if (isRec) {
        await axios.post(`/api/channels/${channel.id}/recording/stop`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRecordingStatus(prev => ({ ...prev, [channel.id]: false }));
        setRecordingStartTimes(prev => { const n = { ...prev }; delete n[channel.id]; return n; });
        setRecordingElapsed(prev => { const n = { ...prev }; delete n[channel.id]; return n; });
        toast.success('Grabación detenida — VOD creado en la biblioteca');
      } else {
        const res = await axios.post(`/api/channels/${channel.id}/recording/start`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const startedAt = res.data?.info?.startedAt || new Date().toISOString();
        setRecordingStatus(prev => ({ ...prev, [channel.id]: true }));
        setRecordingStartTimes(prev => ({ ...prev, [channel.id]: startedAt }));
        toast.success('Grabación iniciada');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al controlar la grabación');
    }
  };

  const toggleRecordingEnabled = async (channel) => {
    const token = localStorage.getItem('token');
    const newValue = !channel.recording_enabled;
    try {
      await axios.put(`/api/channels/${channel.id}`, { recording_enabled: newValue }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannels(prev => prev.map(c => c.id === channel.id ? { ...c, recording_enabled: newValue } : c));
      toast.success(newValue ? 'Grabación automática activada' : 'Grabación automática desactivada');
    } catch (err) {
      toast.error('Error al actualizar configuración');
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success(`${label} copiado al portapapeles`);
      } else {
        // Fallback para contextos no seguros (HTTP)
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        try {
          document.execCommand('copy');
          toast.success(`${label} copiado al portapapeles`);
        } catch (err) {
          console.error('Error al copiar:', err);
          toast.error('No se pudo copiar al portapapeles');
        }
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error('Error al copiar:', error);
      toast.error('No se pudo copiar al portapapeles');
    }
  };

  const toggleExpanded = (channelId) => {
    setExpandedChannels(prev => ({
      ...prev,
      [channelId]: !prev[channelId]
    }));
  };

  const getProtocolUrl = (channel, protocol) => {
    if (!channel.urls || !channel.urls[protocol]) return 'N/A';
    const protocolData = channel.urls[protocol];
    
    switch(protocol) {
      case 'rtmp':
        return protocolData.url;
      case 'hls':
        return protocolData.playlist;
      default:
        return protocolData.url || 'N/A';
    }
  };

  const getStreamKey = (channel) => {
    // If the channel has a secure publish_key, show that for OBS.
    // Otherwise fall back to legacy stream_key-obs format.
    if (channel.publish_key) return `${channel.publish_key}-obs`;
    return channel.stream_key ? `${channel.stream_key}-obs` : 'N/A';
  };

  const openEmbedModal = (channel) => {
    setSelectedChannelForEmbed(channel);
    setShowEmbedModal(true);
  };

  const openChatModal = async (channel) => {
    setChatModalChannel(channel);
    setChatLoading(true);
    setChatMessages([]);
    try {
      const res = await axios.get(`/api/chat/${channel.id}`);
      setChatMessages(res.data.messages || []);
    } catch (_) {}
    setChatLoading(false);
  };

  const deleteChatMessage = async (messageId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/chat/${chatModalChannel.id}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages(msgs => msgs.filter(m => m.id !== messageId));
    } catch (_) {
      toast.error('Error eliminando mensaje');
    }
  };

  const clearAllChatMessages = async () => {
    if (!window.confirm('¿Eliminar todos los mensajes del chat?')) return;
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`/api/chat/${chatModalChannel.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChatMessages([]);
      toast.success('Chat limpiado');
    } catch (_) {
      toast.error('Error limpiando chat');
    }
  };

  const getEmbedUrl = (channelId, opts = embedOptions) => {
    const host = window.location.origin;
    const params = new URLSearchParams({
      channel: channelId,
      autoplay: opts.autoplay,
      muted: opts.muted,
      chat: opts.chat ? '1' : '0',
      theme: opts.theme
    });
    return `${host}/player?${params.toString()}`;
  };

  const getIframeWidth = (opts = embedOptions) =>
    opts.responsive ? '100%' : `${opts.widthValue}${opts.widthUnit}`;

  const getIframeCode = (channelId, opts = embedOptions) => {
    const w = getIframeWidth(opts);
    const h = `${opts.height}px`;
    return `<iframe\n  src="${getEmbedUrl(channelId, opts)}"\n  width="${w}" height="${h}"\n  frameborder="0"\n  allowfullscreen\n  allow="autoplay; encrypted-media"\n  style="border-radius:8px"\n></iframe>`;
  };

  const copyEmbed = (field) => {
    const text = field === 'url'
      ? getEmbedUrl(selectedChannelForEmbed.id)
      : getIframeCode(selectedChannelForEmbed.id);
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  const getHlsUrl = (channel) => {
    const host = window.location.origin;

    // Todos los canales (VOD y Live) usan la URL UNIFICADA
    // VOD usa el mismo sistema 24/7 (FFmpeg → HLS → unified proxy)
    if (channel.stream_key) {
      return `${host}/hls/${channel.stream_key}-master.m3u8`;
    }

    return null;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex justify-center items-center h-screen">
          <div className="relative">
            <div className="w-16 h-16 border-4 border-accent-purple/30 border-t-accent-purple rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-t-accent-indigo rounded-full animate-spin" style={{ animationDuration: '1.5s', animationDirection: 'reverse' }}></div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-2.5 min-h-screen max-w-[1600px] mx-auto">
        {/* Header with gradient text */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-white">
              {t('channels')}
            </h1>
            <p className="text-gray-400 text-base">
              Gestiona tus canales de streaming
            </p>
          </div>
          <button className="btn-primary-gradient flex items-center gap-2"
                  onClick={() => setShowModal(true)}>
            <Plus size={20} />
            {t('createChannel')}
          </button>
        </div>

        {channels.length === 0 ? (
          <div className="glass-card text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 rounded-xl bg-gradient-to-br from-accent-purple/20 to-accent-indigo/20 backdrop-blur-sm flex items-center justify-center border border-accent-purple/30">
              <Radio size={48} className="text-accent-purple opacity-50" />
            </div>
            <h3 className="text-2xl font-semibold mb-2 text-white">
              No hay canales
            </h3>
            <p className="text-gray-400 mb-8">
              Crea tu primer canal para comenzar
            </p>
            <button className="btn-primary-gradient flex items-center gap-2 mx-auto"
                    onClick={() => setShowModal(true)}>
              <Plus size={20} />
              Crear Primer Canal
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {channels.map((channel) => {
              const isLive = liveStatus[channel.id]?.isLive || hlsStatus[channel.id] || false;
              const isTranscoding = channel.type === 'vod' && channel.playlist_id && !hlsStatus[channel.id];
              const proto = selectedProtocol[channel.id] || 'rtmp';

              return (
                <div key={channel.id} className="glass-card !p-0">

                  {/* ── Top section: icon + name + actions ─────────────── */}
                  <div className="flex items-start gap-3 sm:gap-5 px-4 sm:px-6 pt-6 pb-5">
                    {/* Channel icon */}
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-purple to-accent-indigo
                                    flex items-center justify-center flex-shrink-0
                                    shadow-lg shadow-accent-purple/30">
                      <Radio className="text-white" size={28} />
                    </div>

                    {/* Name + description */}
                    <div className="flex-1 min-w-0 pt-1">
                      <h3 className="text-xl font-bold text-white leading-tight truncate">
                        {channel.name}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {channel.description || 'Sin descripción'}
                      </p>
                    </div>

                    {/* Action buttons — REC+Preview hidden on mobile */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isLive && (
                        <>
                          {/* REC button — desktop only */}
                          {recordingStatus[channel.id] ? (
                            <button
                              onClick={() => toggleRecording(channel)}
                              title="Detener grabación"
                              className="hidden sm:flex items-center gap-1.5 px-2 h-9 rounded-lg border bg-red-500/20 border-red-500/40 text-red-400 hover:bg-red-500/30 transition-all duration-200">
                              <CircleDot size={14} className="animate-pulse shrink-0" />
                              <span className="text-xs font-mono tabular-nums">
                                {recordingElapsed[channel.id] || '00:00:00'}
                              </span>
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleRecording(channel)}
                              title="Grabar stream"
                              className="hidden sm:flex w-9 h-9 rounded-lg border items-center justify-center transition-all duration-200 bg-white/5 border-white/[0.05] hover:bg-white/10 text-gray-400 hover:text-red-400">
                              <CircleDot size={16} />
                            </button>
                          )}
                          {/* Preview button — desktop only */}
                          <button
                            onClick={() => togglePreview(channel.id)}
                            title={previewChannels[channel.id] ? 'Cerrar preview' : 'Ver preview en vivo'}
                            className={`hidden sm:flex w-9 h-9 rounded-lg border items-center justify-center transition-all duration-200 ${
                              previewChannels[channel.id]
                                ? 'bg-accent-purple/20 border-accent-purple/40 text-accent-purple'
                                : 'bg-white/5 border-white/[0.05] hover:bg-white/10 text-gray-400 hover:text-white'
                            }`}>
                            {previewChannels[channel.id] ? <X size={16} /> : <MonitorPlay size={16} />}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => openChatModal(channel)}
                        title="Moderar chat"
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/[0.05]
                                   hover:bg-white/10 text-gray-400 hover:text-indigo-400
                                   flex items-center justify-center transition-all duration-200">
                        <MessageSquare size={16} />
                      </button>
                      <button
                        onClick={() => openEmbedModal(channel)}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/[0.05]
                                   hover:bg-white/10 text-gray-400 hover:text-white
                                   flex items-center justify-center transition-all duration-200">
                        <Share2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteChannel(channel.id)}
                        className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/20
                                   hover:bg-red-500/20 text-red-500
                                   flex items-center justify-center transition-all duration-200">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* ── Status pills row ────────────────────────────────── */}
                  <div className="flex items-center gap-4 px-6 pb-5 flex-wrap">
                    {/* LIVE / OFFLINE / TRANSCODING pill */}
                    {isTranscoding ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
                                       border border-yellow-500/40 bg-yellow-500/10
                                       text-yellow-400 text-xs font-bold uppercase tracking-wider">
                        <Activity size={11} className="animate-pulse" />
                        TRANSCODIFICANDO
                      </span>
                    ) : isLive ? (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
                                       border border-accent-purple/50 bg-accent-purple/10
                                       text-accent-purple text-xs font-bold uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 bg-accent-purple rounded-full animate-pulse" />
                        LIVE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full
                                       border border-accent-purple/30 bg-transparent
                                       text-accent-purple text-xs font-bold uppercase tracking-wider">
                        <span className="w-1.5 h-1.5 bg-accent-purple rounded-full" />
                        LIVE
                      </span>
                    )}

                    {/* Date */}
                    <span className="inline-flex items-center gap-1.5 text-gray-500 text-xs">
                      <Clock size={13} />
                      {new Date(channel.created_at).toLocaleDateString('es-ES')}
                    </span>

                    {/* Connection status */}
                    {isLive ? (
                      <span className="inline-flex items-center gap-1.5 text-accent-emerald text-xs font-semibold uppercase tracking-wider">
                        <Wifi size={13} />
                        ONLINE
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                        <WifiOff size={13} />
                        OFFLINE
                      </span>
                    )}

                    {/* VOD: view stream button */}
                    {channel.type === 'vod' && channel.playlist_id && hlsStatus[channel.id] && (
                      <button className="inline-flex items-center gap-1.5 text-accent-indigo text-xs font-semibold
                                         hover:text-white transition-colors ml-auto"
                              onClick={() => window.open(`/player?channel=${channel.id}`, '_blank')}>
                        <Eye size={13} /> Ver Stream
                      </button>
                    )}
                  </div>

                  {/* ── Mini-player preview ───────────────────────────── */}
                  {previewChannels[channel.id] && isLive && (
                    <div className="mx-6 mb-5">
                      <div className="relative rounded-xl overflow-hidden border border-accent-purple/30 shadow-lg shadow-accent-purple/10">
                        {/* Live badge */}
                        <div className="absolute top-2 left-2 z-10 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/70 backdrop-blur-sm border border-white/10">
                          <span className="w-1.5 h-1.5 bg-red-500 rounded-full animate-pulse" />
                          <span className="text-[10px] font-bold text-white uppercase tracking-wider">En vivo</span>
                        </div>
                        <CustomVideoPlayer
                          key={`preview-${channel.id}`}
                          src={`${window.location.origin}/hls/${channel.stream_key}-master.m3u8`}
                          isLive={true}
                          autoplay={true}
                          muted={true}
                          className="w-full aspect-video"
                        />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-2 text-center">
                        Preview en vivo — {channel.name}
                      </p>
                    </div>
                  )}

                  {/* ── Starting alert ────────────────────────────────── */}
                  {isTranscoding && (
                    <div className="mx-6 mb-5 flex items-start gap-3 px-4 py-3
                                    rounded-xl bg-yellow-500/10 border border-yellow-500/20">
                      <AlertCircle size={16} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-yellow-300">
                        El stream está iniciando — los primeros segmentos HLS pueden tardar unos segundos.
                      </p>
                    </div>
                  )}

                  {/* ── Streaming config (live channels only) ────────────── */}
                  {channel.type !== 'vod' && (
                    <>
                      <div className="mx-6 border-t border-white/[0.05]" />

                      <div className="px-6 py-5 space-y-5">
                        {/* Section title */}
                        <h4 className="text-base font-bold text-white flex items-center gap-2">
                          <TrendingUp size={17} className="text-accent-purple" />
                          Configuración de Streaming
                        </h4>

                        {/* Protocol selector */}
                        <div>
                          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                            Protocolo de Streaming
                          </label>
                          <div className="relative">
                            <select
                              className="w-full appearance-none px-4 py-3 pr-10
                                         rounded-xl bg-white/5 border border-white/[0.05]
                                         text-white text-sm font-medium
                                         focus:outline-none focus:border-accent-purple/50
                                         cursor-pointer transition-colors"
                              value={proto}
                              onChange={(e) => setSelectedProtocol(prev => ({ ...prev, [channel.id]: e.target.value }))}
                            >
                              <option value="rtmp">RTMP (Recomendado para OBS)</option>
                              <option value="hls">HLS (Reproducción)</option>
                            </select>
                            <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                          </div>
                        </div>

                        {proto === 'hls' ? (
                          /* ── HLS URLs ──────────────────────── */
                          <div className="space-y-4">
                            {/* Unified */}
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-widest">
                                  URL Unificada
                                </label>
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-accent-purple/20 text-accent-purple border border-accent-purple/30">
                                  Recomendada
                                </span>
                              </div>
                              <div className="flex items-center rounded-xl bg-black/50 border border-white/[0.05] overflow-hidden">
                                <code className="flex-1 px-4 py-3 text-sm font-mono text-accent-emerald truncate">
                                  {`${window.location.origin}/hls/${channel.stream_key}-master.m3u8`}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(`${window.location.origin}/hls/${channel.stream_key}-master.m3u8`, 'URL Unificada')}
                                  className="px-4 py-3 border-l border-white/[0.05] text-accent-purple hover:bg-accent-purple/10 transition-colors flex-shrink-0">
                                  <Copy size={16} />
                                </button>
                              </div>
                            </div>

                            {/* Playlist */}
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                                URL Playlist 24/7
                              </label>
                              <div className="flex items-center rounded-xl bg-black/50 border border-white/[0.05] overflow-hidden">
                                <code className="flex-1 px-4 py-3 text-sm font-mono text-accent-emerald truncate">
                                  {`${window.location.origin}/hls/${channel.stream_key}-playlist/index.m3u8`}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(`${window.location.origin}/hls/${channel.stream_key}-playlist/index.m3u8`, 'URL Playlist')}
                                  className="px-4 py-3 border-l border-white/[0.05] text-accent-purple hover:bg-accent-purple/10 transition-colors flex-shrink-0">
                                  <Copy size={16} />
                                </button>
                              </div>
                            </div>

                            {/* OBS */}
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                                URL OBS (solo cuando activo)
                              </label>
                              <div className="flex items-center rounded-xl bg-black/50 border border-white/[0.05] overflow-hidden">
                                <code className="flex-1 px-4 py-3 text-sm font-mono text-accent-emerald truncate">
                                  {`${window.location.origin}/hls/${channel.stream_key}-obs/index.m3u8`}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(`${window.location.origin}/hls/${channel.stream_key}-obs/index.m3u8`, 'URL OBS')}
                                  className="px-4 py-3 border-l border-white/[0.05] text-accent-purple hover:bg-accent-purple/10 transition-colors flex-shrink-0">
                                  <Copy size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          /* ── RTMP: Server URL + Stream Key ── */
                          <div className="space-y-5">
                            {/* Server URL */}
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                                URL del Servidor
                              </label>
                              <div className="flex items-center rounded-xl bg-black/50 border border-white/[0.05] overflow-hidden">
                                <code className="flex-1 px-4 py-3 text-sm font-mono text-accent-emerald truncate">
                                  {getProtocolUrl(channel, 'rtmp')}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(getProtocolUrl(channel, 'rtmp'), 'URL del Servidor')}
                                  className="px-4 py-3 border-l border-white/[0.05] text-accent-purple hover:bg-accent-purple/10 transition-colors flex-shrink-0">
                                  <Copy size={16} />
                                </button>
                              </div>
                            </div>

                            {/* Stream Key */}
                            <div>
                              <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-widest mb-2">
                                Stream Key
                              </label>
                              <div className="flex items-center rounded-xl bg-black/50 border border-white/[0.05] overflow-hidden">
                                <code className="flex-1 px-4 py-3 text-sm font-mono text-accent-emerald truncate">
                                  {getStreamKey(channel)}
                                </code>
                                <button
                                  onClick={() => copyToClipboard(getStreamKey(channel), 'Stream Key')}
                                  className="px-4 py-3 border-l border-white/[0.05] text-accent-purple hover:bg-accent-purple/10 transition-colors flex-shrink-0">
                                  <Copy size={16} />
                                </button>
                              </div>
                              {/* Info note */}
                              <p className="text-xs text-gray-500 mt-2.5 flex items-start gap-1.5">
                                <AlertCircle size={13} className="flex-shrink-0 mt-0.5 text-accent-indigo" />
                                <span>
                                  El sufijo <code className="text-accent-emerald font-semibold">-obs</code> es requerido para el sistema de transiciones automáticas OBS ↔ Playlist
                                </span>
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    {/* ── Grabación automática ────────────────────── */}
                    {channel.type === 'live' && (
                      <>
                        <div className="mx-6 border-t border-white/[0.05]" />
                        <div className="px-6 py-4 flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-white flex items-center gap-2">
                              <CircleDot size={14} className="text-red-400" />
                              Grabación automática
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Al conectar OBS, inicia la grabación y crea un VOD automáticamente al desconectar.
                            </p>
                          </div>
                          <button
                            onClick={() => toggleRecordingEnabled(channel)}
                            className={`relative w-11 h-6 rounded-full transition-colors duration-200 flex-shrink-0 ${
                              channel.recording_enabled ? 'bg-red-500' : 'bg-white/10'
                            }`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                              channel.recording_enabled ? 'translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </>
                    )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay-premium"
               onClick={() => setShowModal(false)}>
            <div className="modal-premium max-w-lg"
                 onClick={(e) => e.stopPropagation()}>
              <h2 className="text-2xl font-bold mb-6 bg-gradient-to-r from-white to-accent-purple bg-clip-text text-transparent">
                Crear Nuevo Canal
              </h2>
              <form onSubmit={handleCreateChannel}>
                <div className="mb-5">
                  <label className="block mb-2 font-semibold text-gray-300">
                    Nombre del Canal
                  </label>
                  <input
                    type="text"
                    className="input-glass"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Mi Canal de Streaming"
                    required
                  />
                </div>
                <div className="mb-5">
                  <label className="block mb-2 font-semibold text-gray-300">
                    Descripción
                  </label>
                  <textarea
                    className="input-glass resize-none"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descripción del canal (opcional)"
                    rows={3}
                  />
                </div>
                <div className="mb-5">
                  <label className="block mb-2 font-semibold text-gray-300">
                    Tipo de Canal
                  </label>
                  <select
                    className="input-glass"
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="live">Live Streaming</option>
                    <option value="vod">Video Bajo Demanda (VOD)</option>
                  </select>
                </div>
                {formData.type === 'vod' && (
                  <div className="mb-5">
                    <label className="block mb-2 font-semibold text-gray-300">
                      Playlist
                    </label>
                    <select
                      className="input-glass"
                      value={formData.playlist_id}
                      onChange={(e) => setFormData({ ...formData, playlist_id: e.target.value })}
                    >
                      <option value="">Selecciona una playlist</option>
                      {playlists.map(playlist => (
                        <option key={playlist.id} value={playlist.id}>{playlist.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="flex gap-4 justify-end mt-8">
                  <button
                    type="button"
                    className="btn-glass"
                    onClick={() => setShowModal(false)}
                  >
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary-gradient">
                    Crear Canal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── PA-5: Visual Embed Builder ─────────────────────────────── */}
        {showEmbedModal && selectedChannelForEmbed && (
          <div
            className="fixed inset-0 z-[1500] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
            onClick={() => setShowEmbedModal(false)}
          >
            <div
              className="glass-card w-full max-w-6xl max-h-[92vh] flex flex-col overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-purple to-accent-indigo flex items-center justify-center">
                    <Code2 size={18} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Embed Builder</h2>
                    <p className="text-xs text-gray-400">{selectedChannelForEmbed.name}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowEmbedModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body: 2 columns on desktop, stacked on mobile */}
              <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden overflow-y-auto md:overflow-hidden">

                {/* LEFT: Controls */}
                <div className="w-full md:w-72 md:flex-shrink-0 border-b md:border-b-0 md:border-r border-white/10 overflow-y-auto p-5 space-y-5"
                     style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>

                  {/* Comportamiento */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Sliders size={12} /> Comportamiento
                    </h3>
                    <div className="space-y-2">
                      {[
                        { key: 'autoplay', icon: Play, label: 'Autoplay', hint: 'Requiere muted' },
                        { key: 'muted', icon: VolumeX, label: 'Silenciado', hint: null },
                        { key: 'chat', icon: MessageSquare, label: 'Mostrar chat', hint: null },
                      ].map(({ key, icon: Icon, label, hint }) => (
                        <label key={key} className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-all">
                          <div className="flex items-center gap-2">
                            <Icon size={14} className="text-gray-400" />
                            <div>
                              <span className="text-sm text-white font-medium">{label}</span>
                              {hint && <span className="block text-[10px] text-gray-500">{hint}</span>}
                            </div>
                          </div>
                          <button
                            onClick={() => setEmbedOptions(o => ({ ...o, [key]: !o[key], previewKey: o.previewKey + 1 }))}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                              embedOptions[key] ? 'bg-accent-purple' : 'bg-gray-600'
                            }`}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                              embedOptions[key] ? 'translate-x-4' : 'translate-x-1'
                            }`} />
                          </button>
                        </label>
                      ))}
                    </div>
                  </section>

                  {/* Apariencia */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Monitor size={12} /> Apariencia
                    </h3>
                    <div className="flex gap-2 mb-3">
                      {['dark', 'light'].map(t => (
                        <button
                          key={t}
                          onClick={() => setEmbedOptions(o => ({ ...o, theme: t, previewKey: o.previewKey + 1 }))}
                          className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold border transition-all ${
                            embedOptions.theme === t
                              ? 'bg-accent-purple/20 border-accent-purple text-white'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10'
                          }`}
                        >
                          {t === 'dark' ? <Moon size={12} /> : <Sun size={12} />}
                          {t === 'dark' ? 'Oscuro' : 'Claro'}
                        </button>
                      ))}
                    </div>
                  </section>

                  {/* Tamaño */}
                  <section>
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <Maximize2 size={12} /> Tamaño
                    </h3>

                    {/* Responsive toggle */}
                    <label className="flex items-center justify-between p-2.5 rounded-xl bg-white/5 hover:bg-white/10 cursor-pointer transition-all mb-3">
                      <span className="text-sm text-white font-medium">Responsive (100%)</span>
                      <button
                        onClick={() => setEmbedOptions(o => ({ ...o, responsive: !o.responsive }))}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                          embedOptions.responsive ? 'bg-accent-purple' : 'bg-gray-600'
                        }`}
                      >
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                          embedOptions.responsive ? 'translate-x-4' : 'translate-x-1'
                        }`} />
                      </button>
                    </label>

                    {/* Presets */}
                    <div className="flex gap-1.5 mb-3 flex-wrap">
                      {[
                        { label: '16:9', w: '640', h: '360' },
                        { label: '4:3', w: '640', h: '480' },
                        { label: '1:1', w: '400', h: '400' },
                        { label: '720p', w: '1280', h: '720' },
                      ].map(p => (
                        <button
                          key={p.label}
                          onClick={() => setEmbedOptions(o => ({ ...o, responsive: false, widthValue: p.w, widthUnit: 'px', height: p.h }))}
                          className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-white/5 border border-white/10 text-gray-300 hover:bg-accent-purple/20 hover:border-accent-purple/40 transition-all"
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>

                    {/* Width */}
                    {!embedOptions.responsive && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">Ancho</label>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              value={embedOptions.widthValue}
                              onChange={e => setEmbedOptions(o => ({ ...o, widthValue: e.target.value }))}
                              className="flex-1 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-accent-purple"
                            />
                            <select
                              value={embedOptions.widthUnit}
                              onChange={e => setEmbedOptions(o => ({ ...o, widthUnit: e.target.value }))}
                              className="px-2 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-accent-purple"
                            >
                              <option value="px">px</option>
                              <option value="%">%</option>
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 mb-1 block">Alto (px)</label>
                          <input
                            type="number"
                            value={embedOptions.height}
                            onChange={e => setEmbedOptions(o => ({ ...o, height: e.target.value }))}
                            className="w-full px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm outline-none focus:border-accent-purple"
                          />
                        </div>
                      </div>
                    )}
                  </section>
                </div>

                {/* RIGHT: Preview + Code */}
                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

                  {/* Preview area */}
                  <div className="flex-1 bg-black/30 flex flex-col overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
                      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-1.5">
                        <Monitor size={12} /> Vista Previa
                      </span>
                      <button
                        onClick={() => setEmbedOptions(o => ({ ...o, previewKey: o.previewKey + 1 }))}
                        className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white px-2 py-1 rounded-lg hover:bg-white/10 transition-all"
                        title="Recargar preview"
                      >
                        <RefreshCw size={12} /> Recargar
                      </button>
                    </div>
                    <div className="flex-1 overflow-auto flex items-center justify-center p-4">
                      <div
                        style={{
                          width: embedOptions.responsive ? '100%' : `min(${embedOptions.widthValue}${embedOptions.widthUnit}, 100%)`,
                          height: `${embedOptions.height}px`,
                          maxWidth: '100%',
                          borderRadius: 8,
                          overflow: 'hidden',
                          background: '#000'
                        }}
                      >
                        <iframe
                          key={embedOptions.previewKey}
                          src={getEmbedUrl(selectedChannelForEmbed.id)}
                          width="100%"
                          height="100%"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                          title="Embed Preview"
                          style={{ display: 'block' }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Code section */}
                  <div className="flex-shrink-0 border-t border-white/10 p-4 space-y-3 bg-black/20">

                    {/* URL */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">URL del Reproductor</span>
                        <button
                          onClick={() => copyEmbed('url')}
                          className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-all ${
                            copiedField === 'url'
                              ? 'bg-accent-emerald/20 border-accent-emerald/40 text-accent-emerald'
                              : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          {copiedField === 'url' ? <><Check size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
                        </button>
                      </div>
                      <div className="px-3 py-2 rounded-xl bg-black/40 border border-white/10 font-mono text-xs text-accent-purple break-all select-all">
                        {getEmbedUrl(selectedChannelForEmbed.id)}
                      </div>
                    </div>

                    {/* iframe code */}
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Código iframe</span>
                        <div className="flex gap-2">
                          <button
                            onClick={() => copyEmbed('iframe')}
                            className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-all ${
                              copiedField === 'iframe'
                                ? 'bg-accent-emerald/20 border-accent-emerald/40 text-accent-emerald'
                                : 'bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white'
                            }`}
                          >
                            {copiedField === 'iframe' ? <><Check size={11} /> Copiado</> : <><Copy size={11} /> Copiar</>}
                          </button>
                          <button
                            onClick={() => window.open(getEmbedUrl(selectedChannelForEmbed.id), '_blank')}
                            className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border bg-accent-purple/10 border-accent-purple/30 text-accent-purple hover:bg-accent-purple/20 transition-all"
                          >
                            <ExternalLink size={11} /> Abrir
                          </button>
                        </div>
                      </div>
                      <pre className="px-3 py-2 rounded-xl bg-black/40 border border-white/10 font-mono text-xs text-gray-300 whitespace-pre-wrap break-all select-all overflow-auto max-h-24">
                        {getIframeCode(selectedChannelForEmbed.id)}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Chat Moderation Modal ──────────────────────────────────── */}
        {chatModalChannel && (
          <div
            className="fixed inset-0 z-[1500] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
            onClick={() => setChatModalChannel(null)}
          >
            <div
              className="glass-card w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden rounded-2xl border border-white/10 shadow-2xl"
              onClick={e => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <MessageSquare size={16} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-white">Moderar Chat</h2>
                    <p className="text-xs text-gray-400">{chatModalChannel.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {chatMessages.length > 0 && (
                    <button
                      onClick={clearAllChatMessages}
                      className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all"
                    >
                      <Trash2 size={12} /> Limpiar todo
                    </button>
                  )}
                  <button
                    onClick={() => setChatModalChannel(null)}
                    className="p-2 rounded-lg hover:bg-white/10 text-gray-400 hover:text-white transition-all"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* Messages list */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
                {chatLoading ? (
                  <div className="text-center text-gray-500 py-10 text-sm">Cargando mensajes...</div>
                ) : chatMessages.length === 0 ? (
                  <div className="text-center text-gray-500 py-10 text-sm">Sin mensajes aún.</div>
                ) : (
                  chatMessages.map(msg => (
                    <div key={msg.id} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/8 transition-all">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 mb-0.5">
                          <span className="text-sm font-semibold text-indigo-400 truncate">{msg.name}</span>
                          <span className="text-[10px] text-gray-500 flex-shrink-0">
                            {new Date(msg.ts).toLocaleString('es', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <p className="text-sm text-gray-300 break-words">{msg.text}</p>
                      </div>
                      <button
                        onClick={() => deleteChatMessage(msg.id)}
                        title="Eliminar mensaje"
                        className="flex-shrink-0 p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-all"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 px-5 py-3 border-t border-white/10 text-xs text-gray-500">
                {chatMessages.length} mensaje{chatMessages.length !== 1 ? 's' : ''} · Solo el administrador puede eliminar mensajes
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
}

export default Channels;
